// Deployed merge path retained ONLY while operations are inactive.
// Never a fallback after an atomic operation fails.
import type { SupabaseClient } from '../../lib/supabase';
import type { Lead } from './types';
import type { LeadUpdate } from './queries';
import { readProspectingAvailability } from './rolloutAvailability';
const fail = (message: string): never => {
  throw new Error(message);
};

export async function legacyMergeLeads(
  client: SupabaseClient,
  keeper: Lead,
  loser: Lead,
  updatedBy: string,
): Promise<void> {
  if ((await readProspectingAvailability(client)).operations_enabled)
    fail('The merge workflow changed. Refresh before continuing.');
  const { data: loserMemberships, error: memErr } = await client
    .from('lead_list_memberships')
    .select('id, list_id')
    .eq('lead_id', loser.id);
  if (memErr) {
    fail(memErr.message);
  }
  for (const membership of loserMemberships ?? []) {
    const { error: moveErr } = await client
      .from('lead_list_memberships')
      .update({ lead_id: keeper.id })
      .eq('id', membership.id);
    if (moveErr) {
      // Keeper already sits in this list: drop the duplicate membership.
      const { error: dropErr } = await client
        .from('lead_list_memberships')
        .delete()
        .eq('id', membership.id);
      if (dropErr) {
        fail(dropErr.message);
      }
    }
  }
  const { error: taskErr } = await client
    .from('sales_tasks')
    .update({ lead_id: keeper.id })
    .eq('lead_id', loser.id);
  if (taskErr) {
    fail(taskErr.message);
  }
  const changes: LeadUpdate = { updated_by: updatedBy };
  if (keeper.email === '' && loser.email !== '') {
    changes.email = loser.email;
  }
  if (keeper.phone === '' && loser.phone !== '') {
    changes.phone = loser.phone;
  }
  if (keeper.title === '' && loser.title !== '') {
    changes.title = loser.title;
  }
  if (keeper.company_name === '' && loser.company_name !== '') {
    changes.company_name = loser.company_name;
  }
  const mergedNote = [
    keeper.notes,
    loser.notes,
    loser.email !== '' && loser.email !== keeper.email
      ? `Merged duplicate (${loser.email})`
      : 'Merged duplicate',
  ]
    .filter((part) => part !== '')
    .join('\n');
  changes.notes = mergedNote;
  // The consent record survives a merge, always.
  if (loser.source === 'inbound' && keeper.source !== 'inbound') {
    changes.source = 'inbound';
  }
  // ── And so does the OTHER consent record ─────────────────────────────────
  //
  // The line above says "always" and meant inbound/outbound only. Until Drop
  // 39a this function copied email, phone, title, company and notes, then hard
  // deleted the loser and dropped `closure_reason_id`, `stage_id`,
  // `closed_on` and `bad_contact` on the floor. Merge a Do-not-contact lead
  // into an open duplicate and the strictest fact about her went with the row.
  //
  // The triggers in 20261220000100 cannot save this one. They read the closing
  // reason, and after a merge like that there is no reason left to read.
  //
  // Kyu's call, 29 Aug: consent wins. If either side blocks contact the merged
  // lead carries that reason and is Closed. It CANNOT be carried half-way:
  // leads_closure_reason_guard nulls a reason on any lead that is not Closed,
  // so the stage and the reason move together or neither moves. That means a
  // merge can close a lead somebody was working. It is the right surprise: the
  // alternative is a live lead who asked us to stop.
  //
  // Read from the row rather than the spelling, the same as everywhere else.
  const { data: blockingRows, error: blockErr } = await client
    .from('lead_closure_reasons')
    .select('id')
    .eq('blocks_contact', true);
  if (blockErr) {
    fail(blockErr.message);
  }
  const blocking = new Set((blockingRows ?? []).map((row: { id: string }) => row.id));
  const blocks = (lead: Lead) =>
    lead.closure_reason_id !== null && blocking.has(lead.closure_reason_id);
  if (blocks(loser) && !blocks(keeper)) {
    changes.stage_id = loser.stage_id;
    changes.closure_reason_id = loser.closure_reason_id;
    // Her date, not today's. She said stop when she said it, and re-dating the
    // close to the day somebody tidied up a duplicate would move her into the
    // wrong reporting window for ever.
    changes.closed_on = loser.closed_on;
  }
  // A bad address is a property of the person, not of the row that happened to
  // win. OR-ed rather than copied, so a merge never launders one clean.
  if (loser.bad_contact && !keeper.bad_contact) {
    changes.bad_contact = true;
  }
  // Deleting the loser first frees its unique email for the keeper.
  const { error: deleteErr } = await client.from('leads').delete().eq('id', loser.id);
  if (deleteErr) {
    fail(deleteErr.message);
  }
  const { error: updateErr } = await client.from('leads').update(changes).eq('id', keeper.id);
  if (updateErr) {
    fail(updateErr.message);
  }
}
